import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dedicated password reset flow. Two phases driven by the ?token= query:
 *   • no token  → request-reset form (email entry; generic server response)
 *   • token     → set-new-password form (confirm + submit → auth/password-reset/confirm)
 */
export default function PasswordResetPage() {
  const [mode, setMode] = useState('request'); // 'request' | 'confirm' | 'done'
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get('token');
    if (t) { setToken(t); setMode('confirm'); }
  }, []);

  const submitRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/password-reset/request`, { email });
      toast.success(r.data?.message || 'Check your email.');
      setMode('done-request');
    } catch (err) {
      // Anti-enumeration: backend returns 200 even for unknown emails. A
      // non-200 here is a real validation issue.
      toast.error(err.response?.data?.detail || 'Request failed');
    } finally { setLoading(false); }
  };

  const submitConfirm = async (e) => {
    e.preventDefault();
    if (pw1.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    if (pw1 !== pw2)   { toast.error('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, { token, new_password: pw1 });
      setMode('done-confirm');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally { setLoading(false); }
  };

  const shell = (title, children) => (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #F8F9FB, #FFFFFF)' }}>
      <div className="relative w-full max-w-md">
        <div className="p-8" style={{ background: '#FFFFFF', borderRadius: '16px', boxShadow: '0 8px 24px rgba(14, 15, 18, 0.10)', border: '1px solid #E6E8EF' }}>
          <h1 className="text-3xl font-bold mb-6" style={{ color: '#6C5CE7', fontFamily: 'Space Grotesk' }}>{title}</h1>
          {children}
          <div className="mt-6 text-center">
            <a href="/auth" style={{ color: '#6C5CE7', textDecoration: 'underline', fontSize: '14px' }}>Back to sign in</a>
          </div>
        </div>
      </div>
    </div>
  );

  const inputStyle = { background: '#FFF', border: '1px solid #D1D5DB', color: '#0B0B0B', padding: '12px', borderRadius: '8px', fontSize: '14px', width: '100%' };
  const btnStyle = { width: '100%', padding: '12px', background: '#6C5CE7', color: '#FFF', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 };

  if (mode === 'done-request') return shell('Check your email', (
    <p data-testid="reset-request-done" style={{ color: '#111', fontSize: 14 }}>
      If an account exists for that email, password reset instructions have been sent. The link expires in 1 hour.
    </p>
  ));

  if (mode === 'done-confirm') return shell('Password reset', (
    <>
      <p data-testid="reset-confirm-done" style={{ color: '#111', fontSize: 14, marginBottom: 16 }}>
        Password reset successfully. You can now sign in.
      </p>
      <a href="/auth" style={{ ...btnStyle, display: 'inline-block', textAlign: 'center', textDecoration: 'none' }}>Sign in</a>
    </>
  ));

  if (mode === 'confirm') return shell('Set a new password', (
    <form onSubmit={submitConfirm} className="space-y-4" data-testid="reset-confirm-form">
      <div>
        <label style={{ fontWeight: 600 }}>New password</label>
        <input type="password" data-testid="reset-new-password" value={pw1} onChange={e => setPw1(e.target.value)} required style={inputStyle} />
      </div>
      <div>
        <label style={{ fontWeight: 600 }}>Confirm new password</label>
        <input type="password" data-testid="reset-confirm-password" value={pw2} onChange={e => setPw2(e.target.value)} required style={inputStyle} />
      </div>
      <button type="submit" disabled={loading} data-testid="reset-submit-btn" style={btnStyle}>
        {loading ? 'Resetting…' : 'Reset Password'}
      </button>
    </form>
  ));

  return shell('Forgot password', (
    <form onSubmit={submitRequest} className="space-y-4" data-testid="reset-request-form">
      <p style={{ color: '#555', fontSize: 13, marginBottom: 8 }}>
        Enter your account email. We'll send you a reset link if an account exists.
      </p>
      <div>
        <label style={{ fontWeight: 600 }}>Email</label>
        <input type="email" data-testid="reset-email-input" value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} />
      </div>
      <button type="submit" disabled={loading} data-testid="reset-request-submit-btn" style={btnStyle}>
        {loading ? 'Sending…' : 'Send reset link'}
      </button>
    </form>
  ));
}
